import { useCallback, useContext, useEffect } from 'react';
import { useDashboardEdit, dispatchDashboardEdit } from 'adapter/in/dashboards/context/EditDashboard';
import { isSome } from 'fp-ts/Option';
import { ToasterContext, ToastType } from '@dexma/ui-components';
import { useUpdateNameDashboard } from 'adapter/in/dashboards/hooks/Dashboard/useUpdateDashboard';
import i18n from 'asset/translation';

const useEditNameModalContainer = (): {
    dashboardOriginalName: string;
    onSubmit: (name: string) => void;
    showModal: boolean;
    onModalCancel: () => void;
    isLoading: boolean;
    error: boolean;
    success: boolean;
    reset: () => void;
} => {
    const { dashboard, showModalEditName } = useDashboardEdit();
    const { setShowModalEditName, setDashboardNameAndCloseModal } = dispatchDashboardEdit();
    const { update, isLoading, success, data, error, reset: updateReset } = useUpdateNameDashboard();
    const toastContext: { toast: (options: { text: string; type: ToastType }) => void } = useContext(ToasterContext);
    const dashboardOriginalName = isSome(dashboard) ? dashboard.value.name : '';

    useEffect(() => {
        if (data) {
            setDashboardNameAndCloseModal(data.name);
            toastContext.toast({
                text: i18n.t('dashboards.edit.saveDashboardNameSuccess'),
                type: ToastType.SUCCESS,
            });
        }
    }, [data]);

    useEffect(() => {
        if (!showModalEditName && (error || success)) {
            updateReset();
        }
    }, [updateReset, showModalEditName, error, success]);

    const handleOnModalCancel = () => setShowModalEditName(false);
    const handleOnSubmit = useCallback((name: string) => isSome(dashboard) && update(dashboard.value.copyWith({ name })), [dashboard]);

    return {
        success,
        dashboardOriginalName,
        onSubmit: handleOnSubmit,
        showModal: showModalEditName,
        onModalCancel: handleOnModalCancel,
        isLoading,
        error,
        reset: updateReset,
    };
};

export default useEditNameModalContainer;
